import { useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useScheduleSubscription, markPollRefresh, markPollComplete } from './useScheduleSubscription';
import { useProceduresBatchSubscription } from './useProceduresBatchSubscription';
// import { useScheduleProcedureSubscriptions } from './useScheduleProcedureSubscriptions'; // Disabled - causes connection overload

/**
 * Schedule subscription manager using ECST (Event-Carried State Transfer) pattern.
 *
 * ARCHITECTURE:
 * - Single scheduleUpdated subscription (1 WebSocket connection)
 * - Events carry complete procedure data (no refetch needed)
 * - 2-second backend cache for instant updates
 * - Polling safety net (30s) for missed events
 *
 * UPDATE STRATEGY:
 * 1. Real-time subscriptions (instant updates)
 *    - procedures: Real-time procedure updates via subscription (auto-updates session context)
 *    - scheduleUpdatedIncremental: Incremental procedure updates (2s cache)
 *    - proceduresBatchUpdated: Batch updates with procedure IDs (CSV uploads)
 * 2. Polling safety net (30s interval)
 *    - Only refetches queries (not subscriptions - they auto-update)
 *    - Ensures consistency even if WebSocket drops events
 *
 * PERFORMANCE:
 * - 1 WebSocket connection (vs 71+ with per-procedure subscriptions)
 * - 1 database query with 2s cache (vs 71 queries)
 * - Scales to unlimited schedule size
 * - 2-second update delay is imperceptible to users
 *
 * Usage:
 * ```typescript
 * // All screens use the same subscription strategy
 * useScheduleUpdates({ date, hospitalId });
 * ```
 *
 * See: docs/subscriptions/REALTIME_SUBSCRIPTIONS_BEST_PRACTICES.md
 */

export interface ScheduleUpdatesOptions {
  date: string;
  hospitalId?: number;
  physician?: number;
  pollInterval?: number; // Polling interval in ms (default: 30000)
  enablePolling?: boolean; // Enable polling safety net (default: true)
}

export function useScheduleUpdates({
  date,
  hospitalId,
  physician,
  pollInterval = 30000, // 30 seconds default
  enablePolling = true, // Polling enabled by default
}: ScheduleUpdatesOptions) {
  const client = useApolloClient();
  const [lastEventTime, setLastEventTime] = useState(Date.now());

  // ECST Pattern: Single subscription with complete procedure data
  // - 1 WebSocket connection (scales to unlimited procedures)
  // - 2-second backend cache for instant updates
  // - Events carry full data (no refetch needed)
  useScheduleSubscription({
    date,
    physician,
    enabled: !!date,
  });

  // Batch update subscription (for CSV uploads)
  // Track when events are received to optimize polling
  useProceduresBatchSubscription({
    hospitalId: hospitalId || 0,
    date,
    enabled: !!date && !!hospitalId,
    onRefresh: () => {
      // Update last event time when batch update received
      setLastEventTime(Date.now());
      console.log('[Polling] Batch update event received, resetting poll timer');
    },
  });

  // Polling safety net - ensures schedule stays in sync even if events are missed
  useEffect(() => {
    if (!enablePolling || !date) {
      return;
    }

    console.log(`[Polling] Starting smart polling for ${date} (interval: ${pollInterval}ms)`);

    const interval = setInterval(() => {
      const timeSinceLastEvent = Date.now() - lastEventTime;

      // Only poll if no events received in the last polling interval
      if (timeSinceLastEvent > pollInterval) {
        console.log(
          `[Polling] No events for ${Math.round(timeSinceLastEvent / 1000)}s, refreshing schedule for ${date}`
        );

        // Mark poll refresh BEFORE refetch to create grace period
        // This prevents race conditions while the poll request is in flight
        markPollRefresh();

        // Refetch all schedule-related queries (safety net for missed events)
        // Note: procedures use subscription only (auto-updates with session context)
        client
          .refetchQueries({
            include: ['dailyScheduleEntities'],
          })
          .then(() => {
            // Mark poll COMPLETE after refetch finishes
            // Events with _fetchedAt older than this will be rejected as stale
            markPollComplete();
          })
          .catch(err => {
            console.error('[Polling] Refetch failed:', err);
          });

        // Reset timer after polling
        setLastEventTime(Date.now());
      } else {
        console.log(
          `[Polling] Skipping poll, event received ${Math.round(timeSinceLastEvent / 1000)}s ago (threshold: ${Math.round(pollInterval / 1000)}s)`
        );
      }
    }, pollInterval);

    return () => {
      console.log(`[Polling] Stopping polling for ${date}`);
      clearInterval(interval);
    };
  }, [client, date, pollInterval, enablePolling, lastEventTime]);

  return {};
}
